import React from 'react';
import useGetPartnerProfile from '../../../hooks/useGetPartnerProfile';
import { useAgreementPopupStore } from './agreementPopupStore';

export type AgreementStatus =
  | 'NOT_REQUIRED'
  | 'PENDING'
  | 'PREVIOUSLY_ACCEPTED'
  | 'ACTIVE'
  | 'EXPIRING_SOON'
  | 'RENEWAL_DUE'
  | 'EXPIRED';

/**
 * Agreement state comes from the backend (kycStatus on the partner profile); the backend also
 * refuses locked API calls, so this only decides what the screen offers.
 */
export const useAgreementGate = () => {
  const { data } = useGetPartnerProfile();
  const kycStatus = data?.data?.kycStatus;
  const openPopup = useAgreementPopupStore((s) => s.open);

  const status: AgreementStatus | undefined = kycStatus?.agreementStatus;
  const locked = !!kycStatus?.agreementEsignEnabled && status === 'PENDING';

  return {
    locked,
    status,
    esignEnabled: !!kycStatus?.agreementEsignEnabled,
    validUntil: kycStatus?.agreementValidUntil as string | undefined,
    renewBy: kycStatus?.agreementRenewBy as string | undefined,
    openPopup,
  };
};

/**
 * Everything inside can be seen but not clicked until the agreement is e-signed. Mouse-down is
 * blocked too, since dropdowns open on it. Events from portals (a menu's backdrop) still bubble
 * through here in React, so only events on elements actually inside are blocked.
 */
export const ViewOnlyUntilSigned: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { locked, openPopup } = useAgreementGate();
  if (!locked) {
    return <>{children}</>;
  }
  const block = (e: React.SyntheticEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.target as Node)) {
      return false;
    }
    e.preventDefault();
    e.stopPropagation();
    return true;
  };
  return (
    <div
      onPointerDownCapture={block}
      onMouseDownCapture={block}
      onClickCapture={(e) => {
        if (block(e)) {
          openPopup();
        }
      }}
    >
      {children}
    </div>
  );
};
